import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Stethoscope } from "lucide-react";
import { formatDate } from "@/lib/date";
import PatientTimelineTab from "@/components/patient-tabs/PatientTimelineTab";
import PatientVitalsTrendsTab from "@/components/patient-tabs/PatientVitalsTrendsTab";
import PatientMedicationsTab from "@/components/patient-tabs/PatientMedicationsTab";
import PatientPersonalMedicalTab from "@/components/patient-tabs/PatientPersonalMedicalTab";
import PatientReportVaultTab from "@/components/patient-tabs/PatientReportVaultTab";
import PatientPrescriptionHistoryTab from "@/components/patient-tabs/PatientPrescriptionHistoryTab";
import PatientAiInsightsTab from "@/components/patient-tabs/PatientAiInsightsTab";
import ConsultationContent from "@/components/ConsultationContent";
import { usePatientEditForm } from "@/hooks/usePatientEditForm";
import { useReportUploadQueue } from "@/hooks/useReportUploadQueue";

export default function PatientProfileContent({ patientId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDoctor = user?.role === "DOCTOR";
  const isStaff = user?.role === "RECEPTIONIST";
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [reports, setReports] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [reportAnalyses, setReportAnalyses] = useState({});
  const [analyzingId, setAnalyzingId] = useState(null);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [viewingPrescription, setViewingPrescription] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [activeTab, setActiveTab] = useState("personal");

  const load = async () => {
    const { data } = await api.get(`/patients/${patientId}`);
    setPatient(data);
    const { data: appts } = await api.get("/appointments", { params: { patient_id: patientId } });
    setAppointments(appts.appointments);
    const { data: reps } = await api.get(`/reports/${patientId}`);
    setReports(reps);
    if (isDoctor) {
      const analysisEntries = await Promise.all(
        reps.map((r) => api.get(`/ai/reports/${r.id}/analysis`).then(({ data }) => [r.id, data]).catch(() => [r.id, null]))
      );
      setReportAnalyses(Object.fromEntries(analysisEntries.filter(([, v]) => v)));
      const { data: pres } = await api.get(`/prescriptions/patient/${patientId}`);
      setPrescriptions(pres);
      const { data: summary } = await api.get(`/ai/patients/${patientId}/summary`);
      setAiSummary(summary);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const {
    form, setForm, editingPersonal, setEditingPersonal, editingMedical, setEditingMedical,
    editTouched, setEditTouched, editFieldErrors, editIsValid, setEditField, saveEdit, cancelEdit,
  } = usePatientEditForm({ patient, patientId, onSaved: load });

  const {
    uploadCategory, setUploadCategory, pendingFiles, handleFilesSelected,
    uploadQueue, isUploading, startUpload, dismissQueue, viewReport,
  } = useReportUploadQueue({ patientId, onComplete: load });

  const archiveToggle = async () => {
    try {
      await api.post(`/patients/${patientId}/${patient.is_archived ? "unarchive" : "archive"}`);
      toast.success(patient.is_archived ? "Patient unarchived" : "Patient archived");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const generateSummary = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/patients/${patientId}/summary`);
      setAiSummary(data);
      toast.success("AI Patient Summary generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to generate AI summary");
    } finally {
      setAiLoading(false);
    }
  };

  const runAnalyze = async (reportId) => {
    setAnalyzingId(reportId);
    try {
      const { data } = await api.post(`/ai/reports/${reportId}/analyze`);
      setReportAnalyses((prev) => ({ ...prev, [reportId]: data }));
      setExpandedReportId(reportId);
      toast.success("Report analyzed — AI Summary, Timeline, Vitals & Alerts are updating");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to analyze report");
    } finally {
      setAnalyzingId(null);
    }
  };

  const saveParamEdit = async (reportId, parameter, newValue) => {
    try {
      const { data } = await api.put(`/ai/reports/${reportId}/parameters`, { parameter, new_value: newValue });
      setReportAnalyses((prev) => ({ ...prev, [reportId]: data }));
      toast.success("Value updated and marked as manually verified");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save value");
    }
  };

  if (!patient) return <p className="text-slate-500">Loading patient...</p>;

  const presentCategories = [...new Set(reports.map((r) => r.category_detected).filter(Boolean))];
  const withDoctorAppointment = appointments.find((a) => a.status === "WITH_DOCTOR");
  const filteredReports = categoryFilter === "All" ? reports : reports.filter((r) => r.category_detected === categoryFilter);

  return (
    <div data-testid="patient-profile">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="patient-profile-name">
            {patient.full_name}
          </h1>
          <p className="mt-1 font-mono text-sm text-slate-500">{patient.patient_code}</p>
          {patient.flags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="patient-profile-flags">
              {patient.flags.map((f) => (
                <span key={f.key} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {f.emoji} {f.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {isDoctor && (
            <Button data-testid="archive-patient-btn" variant="outline" className="gap-2" onClick={archiveToggle}>
              {patient.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              {patient.is_archived ? "Unarchive" : "Archive Patient"}
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1.5 rounded-xl bg-slate-100 p-1.5">
          <TabsTrigger value="personal" data-testid="tab-personal" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">Personal &amp; Medical</TabsTrigger>
          {isDoctor && <TabsTrigger value="ai" data-testid="tab-ai" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">AI Insights</TabsTrigger>}
          {isDoctor && <TabsTrigger value="timeline" data-testid="tab-timeline" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">AI Patient Timeline</TabsTrigger>}
          {isDoctor && <TabsTrigger value="vitals" data-testid="tab-vitals" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">AI Vitals &amp; Trends</TabsTrigger>}
          <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">AI Report Vault</TabsTrigger>
          {isDoctor && <TabsTrigger value="medications" data-testid="tab-medications" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">AI Medications Management</TabsTrigger>}
          {isDoctor && <TabsTrigger value="prescriptions" data-testid="tab-prescriptions" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">Prescription History</TabsTrigger>}
          <TabsTrigger value="appointments" data-testid="tab-appointments" className="rounded-lg px-4 py-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-sm">Appointments</TabsTrigger>
        </TabsList>

        {isDoctor && withDoctorAppointment && (
          <TabsList className="mt-2 h-auto w-fit rounded-xl border border-teal-200 bg-teal-50 p-1.5">
            <TabsTrigger
              value="consultation"
              data-testid="tab-consultation"
              className="gap-2 rounded-lg px-5 py-2 font-semibold text-teal-800 hover:bg-teal-100 data-[state=active]:bg-teal-700 data-[state=active]:text-white data-[state=active]:shadow-md group"
            >
              <Stethoscope className="h-4 w-4" />
              AI Consultation &amp; Digital Prescription
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500 group-data-[state=active]:bg-white" />
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="personal">
          <PatientPersonalMedicalTab
            patient={patient}
            form={form}
            setForm={setForm}
            editingPersonal={editingPersonal}
            setEditingPersonal={setEditingPersonal}
            editingMedical={editingMedical}
            setEditingMedical={setEditingMedical}
            editTouched={editTouched}
            setEditTouched={setEditTouched}
            editFieldErrors={editFieldErrors}
            editIsValid={editIsValid}
            setEditField={setEditField}
            saveEdit={saveEdit}
            cancelEdit={cancelEdit}
          />
        </TabsContent>

        {isDoctor && withDoctorAppointment && (
          <TabsContent value="consultation">
            <ConsultationContent
              patientId={patientId}
              appointmentId={withDoctorAppointment.id}
              patient={patient}
              onSaved={async () => {
                await load();
                setActiveTab("prescriptions");
              }}
            />
          </TabsContent>
        )}

        <TabsContent value="appointments">
          <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
            <h2 className="font-semibold text-slate-900">Appointment History</h2>
            <div className="mt-4 space-y-3">
              {appointments.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`appt-history-${a.appointment_code}`}>
                  <div>
                    <p className="font-mono text-xs text-slate-500">{a.appointment_code}</p>
                    <p className="text-sm text-slate-900">{formatDate(a.appointment_date)} at {a.appointment_time}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <TypeBadge type={a.appointment_type} />
                    <StatusBadge status={a.status} />
                    {isDoctor && a.status === "WITH_DOCTOR" && (
                      <Button size="sm" data-testid={`consult-appointment-btn-${a.id}`} className="bg-teal-700 hover:bg-teal-800" onClick={() => navigate(`/doctor/patients/${patientId}/consult/${a.id}`)}>
                        Consult
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {appointments.length === 0 && <p className="text-sm text-slate-400">No appointments yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="reports">
          <PatientReportVaultTab
            isStaff={isStaff}
            isDoctor={isDoctor}
            uploadCategory={uploadCategory}
            setUploadCategory={setUploadCategory}
            pendingFiles={pendingFiles}
            handleFilesSelected={handleFilesSelected}
            isUploading={isUploading}
            startUpload={startUpload}
            uploadQueue={uploadQueue}
            dismissQueue={dismissQueue}
            presentCategories={presentCategories}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            filteredReports={filteredReports}
            reports={reports}
            reportAnalyses={reportAnalyses}
            expandedReportId={expandedReportId}
            setExpandedReportId={setExpandedReportId}
            analyzingId={analyzingId}
            runAnalyze={runAnalyze}
            viewReport={viewReport}
            saveParamEdit={saveParamEdit}
          />
        </TabsContent>

        {isDoctor && (
          <TabsContent value="prescriptions">
            <PatientPrescriptionHistoryTab
              prescriptions={prescriptions}
              viewingPrescription={viewingPrescription}
              setViewingPrescription={setViewingPrescription}
              navigate={navigate}
            />
          </TabsContent>
        )}

        {isDoctor && (
          <TabsContent value="medications">
            <PatientMedicationsTab patientId={patientId} />
          </TabsContent>
        )}

        {isDoctor && (
          <TabsContent value="vitals">
            <PatientVitalsTrendsTab patientId={patientId} />
          </TabsContent>
        )}

        {isDoctor && (
          <TabsContent value="timeline">
            <PatientTimelineTab patientId={patientId} />
          </TabsContent>
        )}

        <TabsContent value="ai">
          <PatientAiInsightsTab aiSummary={aiSummary} aiLoading={aiLoading} generateSummary={generateSummary} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
